import React, { useMemo, useState } from 'react';
import { Alert, Box, Chip, Stack, TextField, Typography } from '@mui/material';
import FieldSpecDetail from './FieldSpecDetail';
import CodeSnippetRenderer from '../../../components/CodeSnippetRenderer';
import appConfig from '../../../config/appConfig';
import {
  buildQualifiedPath,
  filterPathEntries,
  flattenCatalogTree,
  getMappingStatus,
  getNodeAtPath,
  normalizeFieldCatalog,
} from '../utils/fieldCatalogUtils';
import { readPathPartyColumnsFromRaw } from '../utils/dslPayloadBuilder';

const CatalogDetailNode = ({ name, node, parentPath, rawConfig, searchQuery }) => {
  const path = buildQualifiedPath(parentPath, name);
  const mappingStatus = getMappingStatus(path, rawConfig);
  const configNode = getNodeAtPath(rawConfig?.fields, path);
  const childEntries = flattenCatalogTree({ [name]: node }, parentPath).filter((entry) => entry.path !== path);
  const filteredChildren = filterPathEntries(childEntries, searchQuery);
  const matchesSelf =
    !searchQuery.trim() ||
    path.toLowerCase().includes(searchQuery.trim().toLowerCase()) ||
    node.kind.toLowerCase().includes(searchQuery.trim().toLowerCase());

  if (node.kind === 'field') {
    if (!matchesSelf) {
      return null;
    }

    return (
      <FieldSpecDetail
        marminPath={path}
        spec={configNode}
        showUnmapped
        required={node.required}
        mappingStatus={mappingStatus}
      />
    );
  }

  if (!matchesSelf && filteredChildren.length === 0) {
    return null;
  }

  return (
    <Box
      sx={{
        mb: 2,
        p: 2,
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
      }}
    >
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 1.5 }}>
        <Chip label={node.kind} size="small" variant="outlined" sx={{ textTransform: 'capitalize' }} />
        <Typography variant="subtitle1" fontWeight={600} sx={{ fontFamily: 'monospace' }}>
          {path}
        </Typography>
        {node.required && <Chip label="Required" size="small" color="warning" />}
        {mappingStatus === 'configured' ? (
          <Chip label="Mapped" size="small" color="success" />
        ) : (
          <Chip label="Not mapped" size="small" />
        )}
      </Stack>

      {node.kind === 'array' && configNode?.source && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          Source:{' '}
          <Typography component="span" sx={{ fontFamily: 'monospace' }}>
            {configNode.source}
          </Typography>
        </Typography>
      )}

      {configNode?.filter && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
            Filter
          </Typography>
          <CodeSnippetRenderer title="" payload={configNode.filter} />
        </Box>
      )}

      <Stack divider={<Box sx={{ borderBottom: 1, borderColor: 'divider', my: 1 }} />} spacing={1}>
        {Object.entries(node.children || {}).map(([childName, childNode]) => (
          <CatalogDetailNode
            key={buildQualifiedPath(path, childName)}
            name={childName}
            node={childNode}
            parentPath={path}
            rawConfig={rawConfig}
            searchQuery={searchQuery}
          />
        ))}
      </Stack>
    </Box>
  );
};

const MappingDetailViewer = ({ rawConfig, compiledConfig, fieldCatalog }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const normalizedCatalog = useMemo(() => normalizeFieldCatalog(fieldCatalog), [fieldCatalog]);
  const flatEntries = useMemo(
    () => filterPathEntries(normalizedCatalog.flatEntries, searchQuery),
    [normalizedCatalog.flatEntries, searchQuery],
  );
  const hasCatalog = Object.keys(normalizedCatalog.tree).length > 0;
  const rootFieldCount = compiledConfig?.root?.fields?.length ?? 0;
  const rootArrayCount = compiledConfig?.root?.arrays?.length ?? 0;
  const pathParty = readPathPartyColumnsFromRaw(rawConfig);

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 1 }}>
        SFTP path profile (from mapping)
      </Typography>
      <Stack direction="row" spacing={1} sx={{ mb: 3 }} flexWrap="wrap" useFlexGap>
        <Chip
          label={`profile column: ${pathParty.profileColumn || '—'} (${pathParty.profileSource || 'not mapped'})`}
          size="small"
          variant="outlined"
        />
        <Chip
          label={`TIN column: ${pathParty.tinColumn || '—'} (${pathParty.tinSource || 'not mapped'})`}
          size="small"
          variant="outlined"
        />
      </Stack>

      {compiledConfig && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Compiled root scope: {rootFieldCount} leaf field(s)
          {rootArrayCount > 0 ? `, ${rootArrayCount} top-level array(s)` : ''} · target{' '}
          <Typography component="span" sx={{ fontFamily: 'monospace' }}>
            {rawConfig?.target || compiledConfig?.target || '—'}
          </Typography>
        </Alert>
      )}

      {hasCatalog && (
        <TextField
          fullWidth
          size="small"
          label="Filter fields"
          placeholder={`Search ${appConfig.brandName} paths…`}
          value={searchQuery}
          onChange={(event) => setSearchQuery(event.target.value)}
          sx={{ mb: 3 }}
        />
      )}

      <Typography variant="h6" sx={{ mb: 1 }}>
        Field tree ({flatEntries.length} paths)
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Request-mappable {appConfig.brandName} fields in nested object/array structure matching the mapping DSL.
      </Typography>

      {!hasCatalog && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          Field catalog unavailable. Showing raw config only is not supported for tree mappings.
        </Alert>
      )}

      {hasCatalog && flatEntries.length === 0 && (
        <Alert severity="info" sx={{ mb: 3 }}>
          No paths match your filter.
        </Alert>
      )}

      {Object.entries(normalizedCatalog.tree).map(([name, node]) => (
        <CatalogDetailNode
          key={name}
          name={name}
          node={node}
          parentPath=""
          rawConfig={rawConfig}
          searchQuery={searchQuery}
        />
      ))}
    </Box>
  );
};

export default MappingDetailViewer;
